import { Link } from '@tanstack/react-router'
import { AlertTriangle, CheckCircle2, Copy, ExternalLink, Loader2, LogOut } from 'lucide-react'
import type { ChangeEvent, ReactNode } from 'react'
import { formatEther, formatUnits } from 'viem'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { publicConfig } from '@/config/publicConfig'
import type { DepositModalModel } from '@/providers/DepositProvider'
import { USDC_DECIMALS } from '@/services/web3/depositContract'

const shortAddress = (value: string) => `${value.slice(0, 6)}…${value.slice(-4)}`
const formatDepositUsdc = (value: bigint | null) =>
  value === null ? '—' : `${formatUnits(value, USDC_DECIMALS)} USDC`
const formatDepositEth = (value: bigint | null) =>
  value === null ? '—' : `${formatEther(value)} ETH`

const ModalBody = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col gap-4 px-5 pb-6 pt-5">{children}</div>
)

const StatusIcon = ({ success = false }: { success?: boolean }) => (
  <div
    className={`mx-auto flex size-14 items-center justify-center rounded-full border ${
      success
        ? 'border-success/30 bg-success/10 text-success'
        : 'border-primary/30 bg-primary/10 text-primary'
    }`}
  >
    {success ? <CheckCircle2 className="size-7" /> : <Loader2 className="size-7 animate-spin" />}
  </div>
)

const HashActions = ({ hash }: { hash: string }) => {
  const explorerUrl = `${publicConfig.web3Deposit.explorerTxBaseUrl}${hash}`
  return (
    <div className="grid grid-cols-2 gap-2">
      <Button
        type="button"
        variant="outline"
        onClick={() => void navigator.clipboard.writeText(hash)}
      >
        <Copy className="mr-2 size-4" /> Copy hash
      </Button>
      <Button type="button" variant="outline" asChild>
        <a href={explorerUrl} target="_blank" rel="noreferrer">
          <ExternalLink className="mr-2 size-4" /> Explorer
        </a>
      </Button>
    </div>
  )
}

const DisconnectWalletButton = ({ model }: { model: DepositModalModel }) => (
  <button
    type="button"
    disabled={model.disconnecting}
    onClick={() => void model.disconnect()}
    className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-danger transition-colors hover:bg-danger/10 hover:text-danger disabled:cursor-wait disabled:opacity-60"
  >
    {model.disconnecting ? (
      <Loader2 className="size-3 animate-spin" />
    ) : (
      <LogOut className="size-3" />
    )}
    {model.disconnecting ? 'Disconnecting…' : 'Disconnect wallet'}
  </button>
)

export const DepositModal = ({ model }: { model: DepositModalModel }) => {
  const config = publicConfig.web3Deposit
  const onAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value
    if (next === '' || /^\d+(?:\.\d{0,6})?$/.test(next)) model.setAmount(next)
  }

  const renderState = () => {
    if (model.phase === 'disconnected') {
      return (
        <ModalBody>
          <div>
            <p className="text-sm font-semibold text-[color:var(--color-text-2)]">
              Connect your wallet
            </p>
            <p className="mt-1 text-xs text-[color:var(--color-text-0)]">
              Connect a wallet to deposit USDC to your DORA account.
            </p>
          </div>
          <div className="space-y-2">
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-[color:var(--color-text-0)]">
              Supported wallet
            </p>
            {model.walletAvailable === false ? (
              <div className="rounded-xl border border-warning/30 bg-warning/10 p-4 text-sm">
                <p className="font-semibold text-[color:var(--color-text-2)]">
                  MetaMask was not found
                </p>
                <a
                  className="mt-2 inline-flex text-primary underline underline-offset-4"
                  href="https://metamask.io/download/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Install MetaMask <ExternalLink className="ml-1 size-3" />
                </a>
              </div>
            ) : (
              <button
                type="button"
                disabled={model.walletAvailable === null}
                onClick={() => void model.connect()}
                className="group flex w-full items-center gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-layer-3)]/40 px-4 py-3.5 text-left transition-colors hover:border-primary/40 disabled:opacity-60"
              >
                <span className="flex size-9 items-center justify-center rounded-xl border border-warning/20 bg-warning/10">
                  <img src="/assets/images/auth/metamask-icon.svg" alt="" className="size-6" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-[color:var(--color-text-2)]">
                    MetaMask
                  </span>
                  <span className="block text-xs text-[color:var(--color-text-0)]">
                    Browser extension wallet
                  </span>
                </span>
                <span className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-[#fff]">
                  {model.walletAvailable === null ? 'Finding…' : 'Connect'}
                </span>
              </button>
            )}
          </div>
          <p className="text-center text-xs text-[color:var(--color-text-0)]">
            Only MetaMask is supported at this time.
          </p>
        </ModalBody>
      )
    }

    if (model.phase === 'wrong-network') {
      return (
        <ModalBody>
          <div className="flex items-center justify-between gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-layer-3)]/40 px-3.5 py-2.5 text-xs">
            <span className="min-w-0 truncate font-mono text-[color:var(--color-text-0)]">
              {model.account ? shortAddress(model.account) : '—'}
            </span>
            <DisconnectWalletButton model={model} />
          </div>
          <div>
            <p className="text-sm font-semibold text-[color:var(--color-text-2)]">Wrong network</p>
            <p className="mt-1 text-xs text-[color:var(--color-text-0)]">
              DORA deposits require the {config.networkName} testnet.
            </p>
          </div>
          <div className="space-y-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-layer-3)]/40 px-4 py-3">
            <div className="flex justify-between text-xs">
              <span className="text-[color:var(--color-text-0)]">Connected chain</span>
              <span className="font-mono text-[color:var(--color-text-2)]">
                {model.chainId ?? 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-[color:var(--color-text-0)]">Required network</span>
              <span className="font-mono text-primary">{config.networkName}</span>
            </div>
          </div>
          <Button className="h-11 w-full" onClick={() => void model.switchNetwork()}>
            Switch to {config.networkName}
          </Button>
        </ModalBody>
      )
    }

    if (model.phase === 'deposit-form') {
      const canSubmit = Boolean(
        model.account && model.usdcBalance !== null && !model.amountError && model.amount,
      )
      return (
        <ModalBody>
          <div className="flex items-center gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-layer-3)]/40 px-3.5 py-2.5 text-xs">
            <span className="min-w-0 flex-1 truncate font-mono text-[color:var(--color-text-0)]">
              {model.account ? shortAddress(model.account) : '—'}
            </span>
            <span className="flex shrink-0 items-center gap-1.5 text-[color:var(--color-text-0)]">
              <span className="size-1.5 rounded-full bg-success" /> {config.networkName}
            </span>
            <DisconnectWalletButton model={model} />
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-[color:var(--color-text-0)]">Wallet balance</span>
            <span className="font-mono text-[color:var(--color-text-2)]">
              {formatDepositUsdc(model.usdcBalance)}
            </span>
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="deposit-usdc-amount"
              className="text-[10px] font-medium uppercase tracking-[0.16em] text-[color:var(--color-text-0)]"
            >
              Amount
            </label>
            <div
              className={`flex items-center gap-2 rounded-xl border bg-[color:var(--color-layer-3)] px-4 py-3 focus-within:border-primary/50 ${
                model.amountError ? 'border-danger/50' : 'border-[color:var(--color-border)]'
              }`}
            >
              <input
                id="deposit-usdc-amount"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0.000000"
                value={model.amount}
                onChange={onAmountChange}
                className="min-w-0 flex-1 bg-transparent font-mono text-base text-[color:var(--color-text-2)] outline-none placeholder:text-[color:var(--color-text-0)]"
              />
              <span className="text-xs text-[color:var(--color-text-0)]">USDC</span>
              <button
                type="button"
                disabled={model.usdcBalance === null}
                onClick={() => model.setAmount(formatUnits(model.usdcBalance ?? 0n, USDC_DECIMALS))}
                className="rounded-md border border-[color:var(--color-border)] px-2 py-0.5 text-xs font-semibold text-primary disabled:opacity-50"
              >
                Max
              </button>
            </div>
            {model.amountError ? (
              <p className="flex items-center gap-1 text-xs text-danger">
                <AlertTriangle className="size-3" /> {model.amountError}
              </p>
            ) : null}
          </div>
          <div className="space-y-2 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-layer-3)]/40 px-4 py-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-[color:var(--color-text-0)]">You receive</span>
              <span className="font-mono text-sm text-[color:var(--color-text-2)]">
                {model.amount && !model.amountError ? `${model.amount} USD` : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-[color:var(--color-text-0)]">ETH balance</span>
              <span className="font-mono text-[color:var(--color-text-0)]">
                {formatDepositEth(model.ethBalance)}
              </span>
            </div>
          </div>
          <p className="text-center text-xs text-[color:var(--color-text-0)]">
            Minimum deposit: <span className="font-mono">{config.minimumUsdc} USDC</span>
          </p>
          <Button className="h-11 w-full" disabled={!canSubmit} onClick={() => void model.submit()}>
            Deposit USDC
          </Button>
        </ModalBody>
      )
    }

    if (
      model.phase === 'preparing' ||
      model.phase === 'wallet-confirm-permit' ||
      model.phase === 'wallet-confirm-tx'
    ) {
      const preparing = model.phase === 'preparing'
      const permit = model.phase === 'wallet-confirm-permit'
      return (
        <ModalBody>
          <StatusIcon />
          <div className="text-center">
            <p className="text-sm font-semibold text-[color:var(--color-text-2)]">
              {preparing
                ? 'Preparing your deposit'
                : permit
                  ? 'Sign the USDC permit'
                  : 'Confirm the deposit'}
            </p>
            <p className="mt-1 text-xs leading-5 text-[color:var(--color-text-0)]">
              {preparing
                ? 'Reading the latest permit nonce and validating single-use deposit instructions.'
                : permit
                  ? 'Review the permit in MetaMask. This signature does not submit a transaction or spend gas.'
                  : `Review the vault transaction in MetaMask. The maximum buffered fee is ${formatDepositEth(model.estimatedMaximumFee)}.`}
            </p>
          </div>
          <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-layer-3)]/40 px-4 py-3 text-xs">
            <div className="flex justify-between">
              <span className="text-[color:var(--color-text-0)]">Amount</span>
              <span className="font-mono text-[color:var(--color-text-2)]">
                {model.amount} USDC
              </span>
            </div>
          </div>
        </ModalBody>
      )
    }

    if (model.phase === 'tx-pending' || model.phase === 'credit-pending') {
      const submitted = model.phase === 'credit-pending'
      return (
        <ModalBody>
          <StatusIcon />
          <div className="text-center" aria-live="polite">
            <p className="text-sm font-semibold text-[color:var(--color-text-2)]">
              {submitted ? 'Crediting your DORA balance' : 'Transaction pending'}
            </p>
            <p className="mt-1 text-xs leading-5 text-[color:var(--color-text-0)]">
              {submitted
                ? `The ${config.networkName} transaction succeeded. Waiting for backend indexing and USD ledger credit.`
                : `Waiting for ${config.requiredConfirmations} ${config.networkName} confirmation${config.requiredConfirmations === 1 ? '' : 's'}.`}
            </p>
          </div>
          {model.txHash ? <HashActions hash={model.txHash} /> : null}
          <Button variant="outline" className="h-11 w-full" asChild>
            <Link to="/portfolio" hash="wallet-deposits" onClick={() => model.setOpen(false)}>
              View deposits
            </Link>
          </Button>
          {submitted ? (
            <Button className="h-11 w-full" onClick={model.reset}>
              Make another deposit
            </Button>
          ) : null}
          <p className="text-center text-xs text-[color:var(--color-text-0)]">
            {submitted
              ? 'You can make another deposit while this one is being credited. Tracking continues in this browser session.'
              : 'You may close this window. Tracking continues in this browser session.'}
          </p>
        </ModalBody>
      )
    }

    if (model.phase === 'completed') {
      return (
        <ModalBody>
          <StatusIcon success />
          <div className="text-center" aria-live="polite">
            <p className="text-sm font-semibold text-[color:var(--color-text-2)]">
              Deposit completed
            </p>
            <p className="mt-1 text-xs leading-5 text-[color:var(--color-text-0)]">
              Your deposit was finalized and the DORA USD balance is refreshing.
            </p>
          </div>
          {model.txHash ? <HashActions hash={model.txHash} /> : null}
          <Button variant="outline" className="h-11 w-full" asChild>
            <Link to="/portfolio" hash="wallet-deposits" onClick={() => model.setOpen(false)}>
              View deposits
            </Link>
          </Button>
          <Button className="h-11 w-full" onClick={model.reset}>
            Make another deposit
          </Button>
        </ModalBody>
      )
    }

    return (
      <ModalBody>
        <div className="mx-auto flex size-14 items-center justify-center rounded-full border border-danger/30 bg-danger/10 text-danger">
          <AlertTriangle className="size-7" />
        </div>
        <div className="text-center" role="alert">
          <p className="text-sm font-semibold text-[color:var(--color-text-2)]">
            {model.error?.title ?? 'Deposit could not continue'}
          </p>
          <p className="mt-1 text-xs leading-5 text-[color:var(--color-text-0)]">
            {model.error?.message}
          </p>
        </div>
        {model.txHash ? <HashActions hash={model.txHash} /> : null}
        {model.txHash && !model.error?.terminal ? (
          <Button className="h-11 w-full" onClick={() => void model.resume()}>
            Resume tracking
          </Button>
        ) : (
          <Button className="h-11 w-full" onClick={model.retry}>
            Try again
          </Button>
        )}
      </ModalBody>
    )
  }

  return (
    <Dialog open={model.open} onOpenChange={model.setOpen}>
      <DialogContent className="max-h-[min(760px,calc(100vh-2rem))] max-w-[448px] gap-0 overflow-y-auto rounded-2xl p-0">
        <DialogHeader className="border-b border-[color:var(--color-border)] px-5 py-4 pr-12 text-left">
          <div className="flex items-center gap-2.5">
            <span className="flex size-7 items-center justify-center rounded-lg bg-primary/10 text-xs font-semibold text-primary">
              $
            </span>
            <DialogTitle className="text-sm">Deposit USDC</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Deposit USDC from MetaMask on {config.networkName} into your DORA account.
          </DialogDescription>
        </DialogHeader>
        {renderState()}
      </DialogContent>
    </Dialog>
  )
}
